import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Stack, Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { colors } from "../../../../theme";

/**
 * CommonPageHeader Component
 * Standardized page title header for AutoBET pages (Lotteries Management styling).
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.title] - Main headline title
 * @param {React.ReactNode} [props.subtitle] - Descriptive subtitle
 * @param {React.ReactNode} [props.actions] - Action buttons or toolbar controls on the right
 * @param {React.ReactNode|Array<{label: string, href?: string, onClick?: Function, icon?: React.ReactNode}>} [props.breadcrumbs] - Optional breadcrumbs or back button
 * @param {React.ReactNode} [props.badge] - Optional status badge next to the title
 * @param {Object} [props.titleSx] - Custom SX overrides for title
 * @param {Object} [props.subtitleSx] - Custom SX overrides for subtitle
 * @param {Object} [props.sx] - Container SX overrides
 * @param {React.ReactNode} [props.children] - Additional child content
 */
const CommonPageHeader = ({
  title,
  subtitle,
  actions,
  breadcrumbs,
  badge,
  titleSx = {},
  subtitleSx = {},
  sx = {},
  children,
}) => {
  const renderBreadcrumbs = () => {
    if (!breadcrumbs) return null;

    if (Array.isArray(breadcrumbs)) {
      return (
        <MuiBreadcrumbs
          separator={<ChevronRight size={14} style={{ color: colors.text.muted || "#64748B" }} />}
          aria-label="breadcrumb"
          sx={{
            mb: 1.25,
            "& .MuiBreadcrumbs-separator": {
              mx: 0.75,
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          {breadcrumbs.map((item, index) => {
            const isLast = index === breadcrumbs.length - 1;

            if (React.isValidElement(item)) {
              return <React.Fragment key={index}>{item}</React.Fragment>;
            }

            if (typeof item === "string") {
              return (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "13px",
                    fontWeight: isLast ? 600 : 500,
                    color: isLast
                      ? (colors.text.primary || "#0F172A")
                      : (colors.text.secondary || "#64748B"),
                  }}
                >
                  {item}
                </Typography>
              );
            }

            if (item && typeof item === "object") {
              const { label, href, onClick, icon } = item;

              if (href && !isLast) {
                return (
                  <Box
                    key={index}
                    component={Link}
                    to={href}
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: colors.text.secondary || "#64748B",
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      transition: "color 0.15s ease",
                      "&:hover": {
                        color: colors.primary.main || "#2563EB",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {icon}
                    {label}
                  </Box>
                );
              }

              if (onClick && !isLast) {
                return (
                  <Box
                    key={index}
                    component="button"
                    onClick={onClick}
                    sx={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: colors.text.secondary || "#64748B",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      transition: "color 0.15s ease",
                      "&:hover": {
                        color: colors.primary.main || "#2563EB",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {icon}
                    {label}
                  </Box>
                );
              }

              return (
                <Typography
                  key={index}
                  sx={{
                    fontSize: "13px",
                    fontWeight: isLast ? 600 : 500,
                    color: isLast
                      ? (colors.text.primary || "#0F172A")
                      : (colors.text.secondary || "#64748B"),
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {icon}
                  {label}
                </Typography>
              );
            }

            return null;
          })}
        </MuiBreadcrumbs>
      );
    }

    return <Box sx={{ mb: 1 }}>{breadcrumbs}</Box>;
  };

  return (
    <Box
      sx={{
        mb: 3.5,
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        flexWrap: "wrap",
        gap: 2,
        ...sx,
      }}
    >
      <Box sx={{ textAlign: "left" }}>
        {renderBreadcrumbs()}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          {title && (
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: colors.text.title,
                fontSize: { xs: "22px", md: "26px" },
                letterSpacing: "-0.5px",
                mb: subtitle ? 0.5 : 0,
                textAlign: "left",
                lineHeight: 1.25,
                ...titleSx,
              }}
            >
              {title}
            </Typography>
          )}
          {badge}
        </Box>

        {subtitle && (
          <Typography
            sx={{
              color: colors.text.secondary,
              fontSize: "14px",
              textAlign: "left",
              lineHeight: 1.5,
              ...subtitleSx,
            }}
          >
            {subtitle}
          </Typography>
        )}

        {children}
      </Box>

      {actions && (
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          {actions}
        </Stack>
      )}
    </Box>
  );
};

CommonPageHeader.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  breadcrumbs: PropTypes.node,
  badge: PropTypes.node,
  titleSx: PropTypes.object,
  subtitleSx: PropTypes.object,
  sx: PropTypes.object,
  children: PropTypes.node,
};

export default CommonPageHeader;
